<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import ImageHoverTile from '@/components/ImageHoverTile.vue'
import DynamicHeading from '@/components/DynamicHeading.vue'
import { useUserStore } from '@/authentication/user.store'
import { wikiToPlainText } from '@/util/tokenize_wiki_text'
import { toolkitUrls } from '@/util/links'
import type { StrategySummary } from './strategy_types'

const props = withDefaults(defineProps<{ strategy: StrategySummary; headingLevel?: number }>(), {
  headingLevel: 2
})

const { isPremium } = storeToRefs(useUserStore())

const locked = computed(() => !isPremium.value && !props.strategy.demo)
const description = computed(() => wikiToPlainText(props.strategy.description))
const strategyUrl = computed(() => toolkitUrls.strategyPage(props.strategy.slug))
</script>

<template>
  <ImageHoverTile :url="strategyUrl" :bg-image-url="strategy.image_url" :locked="locked">
    <template #defaultContent>
      <span class="absolute bottom-0 left-0 block w-full px-6 py-4">
        <DynamicHeading
          :heading-level="headingLevel"
          class="mb-2.5 text-display-sm font-medium leading-[1.33] text-white"
          >{{ strategy.title }}</DynamicHeading
        >
        <span
          v-if="strategy.resource_count"
          class="absolute bottom-4 right-3 text-text-md text-gray-300"
        >
          <span class="material-icons-round align-middle text-text-lg" role="img" :aria-label="`${strategy.resource_count} downloads`">download</span>
          <span aria-hidden="true">{{ strategy.resource_count }}</span>
        </span>
        <span v-if="locked" class="rounded-xs bg-red-300 px-1.5 py-0.5 text-text-sm font-bold text-white">
          <span class="material-icons-round align-middle text-text-sm" aria-hidden="true">lock</span> PREMIUM
        </span>
        <span v-else class="text-text-md text-gray-300">Learn more</span>
      </span>
    </template>
    <template #hoverContent>
      <span class="absolute inset-0 block overflow-hidden px-6 py-4 text-text-lg leading-6 text-gray-400">
        <span class="block font-bold text-white">{{ strategy.title }}</span>
        <span class="line-clamp-3">{{ description }}</span>
      </span>
    </template>
  </ImageHoverTile>
</template>
